import type { QueryClient } from "@tanstack/react-query";
import { QueryKeys } from "@/lib/types";
import type { GetAppointmentBookingResponse } from "@/openapi/generated";
import {
  bookingBelongsToStudent,
  isCanceledBookingStatus,
} from "./appointment-slot";

export const APPOINTMENT_BOOKINGS_BULK_MAX_IDS = 100;

export function appointmentBookingsQueryKey(appointmentId: number) {
  return [QueryKeys.AppointmentBookings, appointmentId] as const;
}

export function uniqueSortedAppointmentIds(appointmentIds: number[]): number[] {
  return [
    ...new Set(appointmentIds.filter((id) => Number.isFinite(id) && id > 0)),
  ].sort((a, b) => a - b);
}

export function appointmentBookingsBulkQueryKey(appointmentIds: number[]) {
  return [
    QueryKeys.AppointmentBookings,
    "bulk",
    uniqueSortedAppointmentIds(appointmentIds).join(","),
  ] as const;
}

/** Best-effort fetch - some appointment booking endpoints 500 on the API. */
export async function fetchAppointmentBookingsSafely(
  fetcher: () => Promise<GetAppointmentBookingResponse[]>,
): Promise<GetAppointmentBookingResponse[]> {
  try {
    const result = await fetcher();
    return Array.isArray(result) ? result : [];
  } catch {
    return [];
  }
}

export async function invalidateAndRefetchAppointmentBookings(
  queryClient: QueryClient,
  appointmentIds?: number[],
) {
  await queryClient.invalidateQueries({
    queryKey: [QueryKeys.AppointmentBookings],
  });

  if (appointmentIds && appointmentIds.length > 0) {
    await Promise.all(
      appointmentIds.map((appointmentId) =>
        queryClient.refetchQueries({
          queryKey: appointmentBookingsQueryKey(appointmentId),
          type: "all",
        }),
      ),
    );
    return;
  }

  await queryClient.refetchQueries({
    queryKey: [QueryKeys.AppointmentBookings],
    type: "all",
  });
}

export function hasStudentBookingForAppointment(
  bookings: GetAppointmentBookingResponse[] | undefined,
  appointmentId: number,
  studentId: number,
): boolean {
  if (!Array.isArray(bookings)) return false;

  return bookings.some((booking) => {
    if (isCanceledBookingStatus(booking.status)) {
      return false;
    }
    return (
      bookingBelongsToStudent(booking, studentId) &&
      Number.isFinite(appointmentId)
    );
  });
}
